/**
 * deck-filter.ts — Pure TypeScript search-syntax engine for filtering deck cards.
 *
 * Supports a Scryfall-inspired query language:
 *   t:creature o:"enters the battlefield" mv:>3 -c:R
 *
 * All predicates are ANDed. Prefix any predicate with `-` to negate.
 * Multi-word values may be quoted with double quotes.
 *
 * No React or UI dependencies — safe for use in workers or tests.
 */

import type { CardMeta } from '@/lib/api';

// ─── Public types ────────────────────────────────────────────────────────────

export interface Predicate {
  /** Whether the predicate result should be inverted. */
  negated: boolean;
  /** Test a single card. Return `true` if the card matches. */
  test: (name: string, meta: CardMeta | undefined, commanders: Set<string>) => boolean;
}

// ─── Tokeniser ───────────────────────────────────────────────────────────────

/**
 * Split a query string on whitespace while respecting double-quoted spans.
 * Quotes are stripped from the returned tokens.
 *
 * Example: `o:"enters the battlefield" t:creature`
 *   → `['o:enters the battlefield', 't:creature']`
 */
function tokenize(query: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  const len = query.length;

  while (i < len) {
    // skip whitespace
    while (i < len && query[i] === ' ') i++;
    if (i >= len) break;

    let token = '';

    // Walk characters, expanding quoted sections inline.
    while (i < len && query[i] !== ' ') {
      if (query[i] === '"') {
        // consume everything until the closing quote (or end)
        i++; // skip opening quote
        while (i < len && query[i] !== '"') {
          token += query[i];
          i++;
        }
        if (i < len) i++; // skip closing quote
      } else {
        token += query[i];
        i++;
      }
    }

    if (token.length > 0) tokens.push(token);
  }

  return tokens;
}

// ─── Internal helpers ────────────────────────────────────────────────────────

/** Case-insensitive substring check. */
function includes(haystack: string | undefined, needle: string): boolean {
  if (!haystack) return false;
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

/** Case-insensitive array membership. */
function arrayIncludes(arr: string[] | undefined, needle: string): boolean {
  if (!arr || arr.length === 0) return false;
  const lower = needle.toLowerCase();
  return arr.some((v) => v.toLowerCase() === lower);
}

/** Parse a stat value, treating `*` as -0.5 for comparison purposes. */
function parseStat(value: string | undefined): number {
  if (value === undefined || value === '') return NaN;
  if (value === '*') return -0.5;
  return Number(value);
}

type NumOp = '>' | '<' | '>=' | '<=' | '=';

/**
 * Parse a numeric comparison string such as `>3`, `<=5`, `=2`, or just `4`.
 * The bare form and `:` prefix both mean equality.
 */
function parseNumericComparison(raw: string): { op: NumOp; value: number } | null {
  let op: NumOp = '=';
  let rest = raw;

  if (rest.startsWith('>=')) {
    op = '>=';
    rest = rest.slice(2);
  } else if (rest.startsWith('<=')) {
    op = '<=';
    rest = rest.slice(2);
  } else if (rest.startsWith('>')) {
    op = '>';
    rest = rest.slice(1);
  } else if (rest.startsWith('<')) {
    op = '<';
    rest = rest.slice(1);
  } else if (rest.startsWith('=')) {
    op = '=';
    rest = rest.slice(1);
  }

  // Treat `*` as -0.5 for power/toughness comparisons
  const value = rest === '*' ? -0.5 : Number(rest);
  if (Number.isNaN(value)) return null;
  return { op, value };
}

/** Evaluate a numeric comparison. */
function evalNumOp(actual: number, op: NumOp, target: number): boolean {
  switch (op) {
    case '>':
      return actual > target;
    case '<':
      return actual < target;
    case '>=':
      return actual >= target;
    case '<=':
      return actual <= target;
    case '=':
      return actual === target;
  }
}

/**
 * Check whether `cardColors` includes every colour letter in `letters`.
 * E.g. color value `"WU"` means the card must have both W and U.
 */
function colorsInclude(cardColors: string[] | undefined, letters: string): boolean {
  if (!cardColors) return false;
  const upper = letters.toUpperCase();
  for (const ch of upper) {
    if (!cardColors.some((c) => c.toUpperCase() === ch)) return false;
  }
  return true;
}

/**
 * Check whether `cardColors` is a subset of the given colour letters.
 * E.g. `ci:WU` means the card's colours must only contain W and/or U.
 */
function colorsSubset(cardColors: string[] | undefined, letters: string): boolean {
  if (!cardColors || cardColors.length === 0) return true; // colourless is a subset of anything
  const allowed = new Set(letters.toUpperCase().split(''));
  return cardColors.every((c) => allowed.has(c.toUpperCase()));
}

// ─── Predicate builders ──────────────────────────────────────────────────────

function typePredicate(value: string): Predicate['test'] {
  return (_name, meta) => {
    if (!meta) return false;
    return (
      includes(meta.type, value) ||
      arrayIncludes(meta.types, value) ||
      arrayIncludes(meta.subtypes, value) ||
      arrayIncludes(meta.supertypes, value)
    );
  };
}

function oraclePredicate(value: string): Predicate['test'] {
  return (_name, meta) => includes(meta?.oracle_text, value);
}

function colorPredicate(value: string): Predicate['test'] {
  return (_name, meta) => colorsInclude(meta?.colors, value);
}

function identityPredicate(value: string): Predicate['test'] {
  return (_name, meta) => colorsInclude(meta?.colorIdentity, value);
}

function ciPredicate(value: string): Predicate['test'] {
  return (_name, meta) => colorsSubset(meta?.colors, value);
}

function numericFieldPredicate(
  raw: string,
  getter: (meta: CardMeta) => number,
): Predicate['test'] | null {
  const cmp = parseNumericComparison(raw);
  if (!cmp) return null;
  return (_name, meta) => {
    if (!meta) return false;
    const actual = getter(meta);
    if (Number.isNaN(actual)) return false;
    return evalNumOp(actual, cmp.op, cmp.value);
  };
}

function isPredicate(value: string): Predicate['test'] {
  const lower = value.toLowerCase();

  switch (lower) {
    case 'legendary':
    case 'legend':
      return (_name, meta) => {
        if (!meta) return false;
        return arrayIncludes(meta.supertypes, 'Legendary') || includes(meta.type, 'Legendary');
      };

    case 'commander':
      return (name, _meta, commanders) => commanders.has(name);

    case 'gamechanger':
    case 'game_changer':
      return (_name, meta) => !!meta?.game_changer;

    case 'banned':
      return (_name, meta) => !!meta?.banned;

    // Quick type shortcuts — common card types
    default: {
      // Treat any other `is:X` as a type shortcut (creature, land, instant, sorcery, etc.)
      const capitalized = lower.charAt(0).toUpperCase() + lower.slice(1);
      return (_name, meta) => {
        if (!meta) return false;
        return arrayIncludes(meta.types, capitalized);
      };
    }
  }
}

function tagPredicate(value: string): Predicate['test'] {
  return (_name, meta) => arrayIncludes(meta?.tags, value);
}

function keywordPredicate(value: string): Predicate['test'] {
  return (_name, meta) => arrayIncludes(meta?.keywords, value);
}

const RARITY_RANK: Record<string, number> = { common: 0, uncommon: 1, rare: 2, mythic: 3 };

/** Resolve a rarity shorthand (c/u/r/m) or full name to the canonical name. */
function resolveRarity(input: string): string | null {
  const lower = input.toLowerCase();
  if (RARITY_RANK[lower] !== undefined) return lower;
  const map: Record<string, string> = { c: 'common', u: 'uncommon', r: 'rare', m: 'mythic' };
  return map[lower] ?? null;
}

function rarityPredicate(raw: string): Predicate['test'] | null {
  // Check for comparison operator: r:>=rare, r:>uncommon, etc.
  const cmpMatch = raw.match(/^([<>=]+)(.+)$/);
  if (cmpMatch) {
    const op = cmpMatch[1] as NumOp;
    if (!['>', '<', '>=', '<=', '='].includes(op)) return null;
    const targetName = resolveRarity(cmpMatch[2]);
    if (!targetName) return null;
    const targetRank = RARITY_RANK[targetName];
    return (_name, meta) => {
      if (!meta?.rarity) return false;
      const cardName = resolveRarity(meta.rarity);
      if (!cardName) return false;
      return evalNumOp(RARITY_RANK[cardName], op, targetRank);
    };
  }
  // Exact match: r:rare or r:r
  const targetName = resolveRarity(raw);
  if (!targetName) return null;
  return (_name, meta) => {
    if (!meta?.rarity) return false;
    const cardName = resolveRarity(meta.rarity);
    return cardName === targetName;
  };
}

function namePredicate(value: string): Predicate['test'] {
  return (name) => includes(name, value);
}

// ─── Token → Predicate ──────────────────────────────────────────────────────

function tokenToPredicate(token: string): Predicate | null {
  // Handle negation prefix
  let negated = false;
  let raw = token;
  if (raw.startsWith('-')) {
    negated = true;
    raw = raw.slice(1);
  }

  if (raw.length === 0) return null;

  // Allow omitting the colon for comparison expressions: pow<=2, mv>3, r>=rare, tou<3
  const colonlessMatch = raw.match(/^(mv|cmc|pow|p|power|tou|toughness|r|rarity)([<>=!]+.+)$/i);
  if (colonlessMatch) {
    // Rewrite as key:value so the normal colon path handles it
    raw = colonlessMatch[1] + ':' + colonlessMatch[2];
  }

  // Split on the FIRST colon only
  const colonIdx = raw.indexOf(':');
  if (colonIdx > 0) {
    const key = raw.slice(0, colonIdx).toLowerCase();
    const value = raw.slice(colonIdx + 1);

    if (value.length === 0) {
      // e.g. `t:` with no value — treat as no-op (matches everything)
      return null;
    }

    let test: Predicate['test'] | null = null;

    switch (key) {
      case 't':
      case 'type':
        test = typePredicate(value);
        break;

      case 'o':
      case 'oracle':
        test = oraclePredicate(value);
        break;

      case 'c':
      case 'color':
        test = colorPredicate(value);
        break;

      case 'id':
      case 'identity':
        test = identityPredicate(value);
        break;

      case 'ci':
        test = ciPredicate(value);
        break;

      case 'mv':
      case 'cmc':
        test = numericFieldPredicate(value, (m) => m.manaValue);
        break;

      case 'pow':
      case 'p':
      case 'power':
        test = numericFieldPredicate(value, (m) => parseStat(m.power));
        break;

      case 'tou':
      case 'toughness':
        test = numericFieldPredicate(value, (m) => parseStat(m.toughness));
        break;

      case 'is':
        test = isPredicate(value);
        break;

      case 'tag':
        test = tagPredicate(value);
        break;

      case 'keyword':
      case 'kw':
        test = keywordPredicate(value);
        break;

      case 'r':
      case 'rarity':
        test = rarityPredicate(value);
        break;

      case 's':
      case 'set':
        test = (_name, meta) => {
          if (!meta) return false;
          return (meta.set_code?.toLowerCase() === value.toLowerCase()) ||
            includes(meta.set_name, value);
        };
        break;

      default:
        // Unknown key — treat the entire token (including key:) as a name search
        test = namePredicate(raw);
        break;
    }

    if (!test) return null;
    return { negated, test };
  }

  // No colon — bare name search
  return { negated, test: namePredicate(raw) };
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Parse a search query string into an array of predicates.
 *
 * Tokens are split on whitespace (respecting double-quoted spans).
 * Each token is converted to a `Predicate` with a `negated` flag and
 * a `test` function.
 *
 * @example
 * ```ts
 * const preds = parseQuery('t:creature mv:>3 -c:R');
 * // preds.length === 3
 * // preds[2].negated === true
 * ```
 */
export function parseQuery(query: string): Predicate[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) return [];

  const tokens = tokenize(trimmed);
  const predicates: Predicate[] = [];

  for (const token of tokens) {
    const pred = tokenToPredicate(token);
    if (pred) predicates.push(pred);
  }

  return predicates;
}

/**
 * Filter a deck card map using a search query string.
 *
 * Returns a new record containing only the cards that match **all**
 * predicates (AND logic). Negated predicates invert their result.
 *
 * If the query is empty or whitespace-only, all cards are returned unchanged.
 *
 * @param cards      - Map of card name → entry (qty, scryfall_id, etc.)
 * @param cardMeta   - Map of card name → metadata from Scryfall / analysis
 * @param commanders - Array of commander card names
 * @param query      - Search query string
 * @returns Filtered subset of `cards`
 *
 * @example
 * ```ts
 * const result = filterCards(deck.cards, meta, ['Kenrith, the Returned King'], 't:creature mv:>3');
 * ```
 */
export function filterCards<T extends { qty: number; scryfall_id?: string }>(
  cards: Record<string, T>,
  cardMeta: Record<string, CardMeta>,
  commanders: string[],
  query: string,
): Record<string, T> {
  const trimmed = query.trim();
  if (trimmed.length === 0) return cards;

  const predicates = parseQuery(trimmed);
  if (predicates.length === 0) return cards;

  const commanderSet = new Set(commanders);
  const result: Record<string, T> = {};

  for (const name of Object.keys(cards)) {
    const meta = cardMeta[name];
    let match = true;

    for (const pred of predicates) {
      const passed = pred.test(name, meta, commanderSet);
      if (pred.negated ? passed : !passed) {
        match = false;
        break;
      }
    }

    if (match) {
      result[name] = cards[name];
    }
  }

  return result;
}
